import type { ReactNode, RefObject } from 'react';
import { ArrowDown } from 'lucide-react';
import { LiksChatMessageList } from '../../components/liks/LiksChatMessageList/LiksChatMessageList';
import { KnowledgeDocumentPreviewModal } from '../../features/knowledge/components/KnowledgeDocumentPreviewModal';
import { useI18n } from '../../i18n/useI18n';
import type { ChatMessage } from '../../types/chatMessage';
import { PlatformChatMessagesStatus } from './PlatformChatMessagesStatus';

type KnowledgeBaseOption = {
  id: string;
  name: string;
};

type PlatformChatTranscriptProps = {
  isMessagesLoading: boolean;
  isMessagesError: boolean;
  hasServerMessages: boolean;
  messages: ChatMessage[];
  messagesRef: RefObject<HTMLDivElement | null>;
  messagesScrollbarClassName: string;
  onMessagesScroll: () => void;
  messageListHeader: ReactNode;
  stickToBottom: boolean;
  currentUserId: string | undefined;
  enableRetry: boolean;
  disableRetry: boolean;
  onRetry: (messageId: string) => void;
  isAtBottom: boolean;
  onScrollToBottom: () => void;
  fallbackKnowledgeBaseId: string | undefined;
  knowledgeBases: KnowledgeBaseOption[] | undefined;
};

export function PlatformChatTranscript({
  isMessagesLoading,
  isMessagesError,
  hasServerMessages,
  messages,
  messagesRef,
  messagesScrollbarClassName,
  onMessagesScroll,
  messageListHeader,
  stickToBottom,
  currentUserId,
  enableRetry,
  disableRetry,
  onRetry,
  isAtBottom,
  onScrollToBottom,
  fallbackKnowledgeBaseId,
  knowledgeBases,
}: PlatformChatTranscriptProps) {
  const { locale, translate } = useI18n();

  return (
    <div className="platform-chat-page__messages-wrap">
      <div
        ref={messagesRef}
        className={`platform-chat-page__messages ${messagesScrollbarClassName}`}
        onScroll={onMessagesScroll}
      >
        {isMessagesLoading && !hasServerMessages ? (
          <PlatformChatMessagesStatus kind="loading" />
        ) : isMessagesError && !hasServerMessages ? (
          <PlatformChatMessagesStatus kind="error" />
        ) : (
          <LiksChatMessageList
            messages={messages}
            header={messageListHeader}
            stickToBottom={stickToBottom}
            currentUserId={currentUserId}
            locale={locale}
            copyLabel={translate('platformChat.copyMessage')}
            copiedLabel={translate('platformChat.copiedMessage')}
            retryLabel={translate('platformChat.retry')}
            downloadImageLabel={translate('platformChat.downloadImage')}
            downloadAttachmentLabel={translate(
              'platformChat.downloadAttachment',
            )}
            expandImageLabel={translate('platformChat.expandImage')}
            closeImagePreviewLabel={translate('platformChat.closeImagePreview')}
            imageLoadingLabel={translate('platformChat.imageLoading')}
            assistantReplyingLabel={translate('platformChat.assistantReplying')}
            generatedImageAltLabel={translate('platformChat.generatedImageAlt')}
            imageGenerationFailedLabel={translate(
              'platformChat.imageGenerationFailed',
            )}
            enableSaveToGallery
            saveToGalleryLabel={translate('platformChat.saveToGallery')}
            sourcesLabel={translate('platformChat.sources')}
            sourcesEmptyLabel={translate('platformChat.sourcesEmpty')}
            sourceTypeColumnLabel={translate('platformChat.sourceTable.type')}
            sourceFileColumnLabel={translate('platformChat.sourceTable.file')}
            sourceUrlColumnLabel={translate('platformChat.sourceTable.url')}
            sourceInaccessibleLabel={translate(
              'platformChat.sourceInaccessible',
            )}
            getSourceTypeLabel={(sourceType) => {
              if (
                sourceType === 'KDB_DOCUMENT' ||
                sourceType === 'CRAWL' ||
                sourceType === 'INTERNET' ||
                sourceType === 'TRAINING_EXAMPLE'
              ) {
                return translate(`platformChat.sourceTypes.${sourceType}`);
              }
              return translate('platformChat.sourceTypes.unknown');
            }}
            openDocumentLabel={translate('platformChat.openDocument')}
            documentPreviewCloseLabel={translate(
              'platformChat.closeDocumentPreview',
            )}
            documentPreviewLoadingLabel={translate(
              'platformChat.documentPreviewLoading',
            )}
            documentPreviewErrorLabel={translate(
              'platformChat.documentPreviewError',
            )}
            documentPreviewInaccessibleLabel={translate(
              'platformChat.documentPreviewInaccessible',
            )}
            documentPreviewUnavailableLabel={translate(
              'platformChat.documentPreviewUnavailable',
            )}
            renderDocumentPreview={(props) => (
              <KnowledgeDocumentPreviewModal {...props} />
            )}
            fallbackKnowledgeBaseId={fallbackKnowledgeBaseId}
            knowledgeBases={knowledgeBases}
            enableRetry={enableRetry}
            disableRetry={disableRetry}
            onRetry={(messageId) => {
              onRetry(messageId);
            }}
          />
        )}
      </div>

      {!isAtBottom && (
        <button
          type="button"
          className="platform-chat-page__scroll-to-bottom"
          aria-label={translate('platformChat.scrollToBottom')}
          onClick={onScrollToBottom}
        >
          <ArrowDown
            className="platform-chat-page__scroll-to-bottom-icon"
            strokeWidth={2}
            aria-hidden
          />
        </button>
      )}
    </div>
  );
}
